// JIM-1007-05 (Jim 2026-10-07): the Consultant slot belongs to EnviroServe
// staff only. Legacy contacts carry role "contractor" for client-side people
// (the GC, the erosion sub), and treating that role as "consultant" put
// 297 client contacts in the Consultant card and offered them as the
// consultant to switch to.

export type SlotContact = {
  role?: string | null;
  is_staff?: boolean | null;
  is_active?: boolean;
  is_primary?: boolean;
};

/** A contact that may fill the Consultant slot: staff in the consultant or
 * contractor role. Legacy rows have no linked user, so they never qualify. */
export function isConsultantSlot(c: SlotContact): boolean {
  const role = (c.role ?? '').toLowerCase();
  return (
    role === 'consultant' || (role === 'contractor' && c.is_staff === true)
  );
}

/** Everyone else who is not an inspector is an on-site contact, including a
 * client-side contractor. */
export function isSiteContact(c: SlotContact): boolean {
  const role = (c.role ?? '').toLowerCase();
  return role !== 'inspector' && !isConsultantSlot(c);
}

/** The person the card shows: the primary staff consultant contact first,
 * else any staff consultant contact. The caller falls back to the site's
 * assigned consultant when this returns undefined. */
export function pickConsultant<T extends SlotContact>(
  contacts: T[] | undefined
): T | undefined {
  const pool = (contacts ?? []).filter(
    (c) => c.is_active !== false && isConsultantSlot(c)
  );
  return pool.find((c) => c.is_primary) ?? pool[0];
}

export function contactRoleLabel(c: SlotContact): string {
  const role = (c.role ?? '').toLowerCase();
  if (role === 'consultant') return 'Consultant';
  if (role === 'contractor') return c.is_staff ? 'Consultant' : 'Contractor';
  if (role === 'inspector') return 'Inspector';
  if (role === 'owner') return 'Owner';
  if (role === 'manager') return 'Manager';
  return role ? role[0].toUpperCase() + role.slice(1) : 'Contact';
}
